import AnalyticsClient, {
  ScreenEventPayload,
  UIEventPayload,
  TrackEventPayload,
  OperationalEventPayload,
  envType,
  tenantType,
  userType,
} from "@atlassiansox/analytics-web-client";
import { useCallback } from "react";
import { getSelectedSiteFromLocalStorage } from "../hooks/useSite";
import { logError } from "../utils/logger";
import { useAnalyticsUserMetadata } from "../bootstrap/AnalyticsUserMetadataProvider";

export const analyticsClient = new AnalyticsClient(
  {
    // local, dev, staging all get sent to the same staging environment
    // https://hello.atlassian.net/wiki/spaces/AAPT/pages/134328422/Analytics+Lesson+2+-+Sending+Events
    env: process.env.ENV === "prod" ? envType.PROD : envType.STAGING,
    product: "rovoExtension",
  },
  {
    // Steps we could take to turn this off: https://hello.atlassian.net/wiki/spaces/I/pages/1217206437/Action+Required+api-private+is+broken+is+your+product+impacted
    useLegacyUrl: true,
    onNewSessionStarted: (): void => {
      void import("./sessionReplay/onAnalyticsWebClientSessionRotation").then((m) =>
        m.onAnalyticsWebClientSessionRotation(),
      );
    },
  },
);

type BaseAnalyticEventParams = {
  // ONLY for events sent via postMessageToParent to node client (e.g. SearchAnalyticsListener).
  // Do NOT add manually when using useSendAnalyticEvent - the web client handles sessionId automatically.
  sessionId?: string;
};

export type SendUIEventParams = BaseAnalyticEventParams & {
  eventType?: "ui";
  event: UIEventPayload;
  userId: string | undefined;
};

export type SendScreenEventParams = BaseAnalyticEventParams & {
  eventType: "screen";
  event: ScreenEventPayload;
  userId: string | undefined;
};

export type SendTrackEventParams = BaseAnalyticEventParams & {
  eventType: "track";
  event: TrackEventPayload;
  userId: string | undefined;
};

export type SendOperationalEventParams = BaseAnalyticEventParams & {
  eventType: "operational";
  event: OperationalEventPayload;
  userId: string | undefined;
};

export type SendAnalyticEventParams =
  | SendUIEventParams
  | SendScreenEventParams
  | SendTrackEventParams
  | SendOperationalEventParams;

type ActionEventType = "ui" | "track" | "operational";

export type SendAnalyticEvent = (params: SendAnalyticEventParams) => void;

export const useSendAnalyticEvent = (): SendAnalyticEvent => {
  const userMetadata = useAnalyticsUserMetadata()?.userMetadata;

  const sendAnalyticEvent = useCallback(
    ({ eventType, event, userId }: SendAnalyticEventParams): void => {
      const site = getSelectedSiteFromLocalStorage();

      const payload = { ...event, attributes: { ...event.attributes, ...userMetadata } };

      if (site?.workspace.cloudId) {
        analyticsClient.setTenantInfo(tenantType.CLOUD_ID, site.workspace.cloudId);
      }

      if (userId) {
        analyticsClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, userId);
      }

      // Use ui as the default type if the event doesn't have a type
      const type = eventType ?? "ui";

      switch (type) {
        case "ui":
          if (isValidActionEvent(payload, type)) {
            analyticsClient.sendUIEvent(payload as UIEventPayload).catch((error) => {
              logError({ error, impact: "Failed to send UI analytics event" });
            });
          }
          break;

        case "screen":
          if ((payload as ScreenEventPayload).name) {
            analyticsClient.sendScreenEvent(payload as ScreenEventPayload).catch((error) => {
              logError({ error, impact: "Failed to send screen analytics event" });
            });
          }
          break;

        case "track":
          if (isValidActionEvent(payload, type)) {
            analyticsClient.sendTrackEvent(payload as TrackEventPayload).catch((error) => {
              logError({ error, impact: "Failed to send track analytics event" });
            });
          }
          break;

        case "operational":
          if (isValidActionEvent(payload, type)) {
            analyticsClient.sendOperationalEvent(payload as OperationalEventPayload).catch((error) => {
              logError({ error, impact: "Failed to send operational analytics event" });
            });
          }
          break;
      }
    },
    [userMetadata],
  );

  return sendAnalyticEvent;
};

function isValidActionEvent(payload: any, type: ActionEventType): boolean {
  if (!payload?.source || !payload.actionSubject || !payload.action) {
    const missingFields = [
      !payload?.source && "source",
      !payload?.actionSubject && "actionSubject",
      !payload?.action && "action",
    ].filter(Boolean);

    logError({
      error: new Error("Invalid analytics action event payload"),
      extra: {
        missingFields,
        type,
        payload,
      },
    });
    return false;
  }

  return true;
}
